import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Archive, ArchiveRestore, Bot, Building2, Globe, Landmark, Pencil, Users } from "lucide-react";
import { z } from "zod";
import {
  DealershipCountrySchema,
  DealershipStatusSchema,
  EinSchema,
  UpdateDealershipSchema,
  countryLabel,
  timezonesForCountry,
  type Dealership,
  type UpdateDealershipDto,
} from "@lumika/shared";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormField,
  Input,
  MobileList,
  MobileListRow,
  PageError,
  PageHeader,
  PageLoading,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Table,
  TableBleed,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
  useZodForm,
} from "@lumika/ui";
import { adminApi } from "../../api/admin";
import { formatDateTime } from "../../lib/format";

const STATUS_VARIANT: Record<Dealership["status"], "default" | "success" | "warning" | "secondary"> = {
  onboarding: "warning",
  pilot: "warning",
  active: "success",
  paused: "secondary",
  inactive: "secondary",
};

// Mirrors the editable subset of `CreateDealershipSchema`. The form
// accepts "" for the optional fields; `toPayload` converts to null,
// which the server schema treats as "unset". Status stays out — it
// keeps its dedicated inline control on the page.
const DealershipFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required.").max(200),
  legalName: z.string().trim().max(200).optional().or(z.literal("")),
  ein: z.union([z.literal(""), EinSchema]).optional(),
  country: DealershipCountrySchema,
  primaryTimezone: z.string().trim().min(1).max(64),
  escalationsEnabled: z.boolean(),
});

type DealershipFormValues = z.infer<typeof DealershipFormSchema>;

function toEditValues(dealership: Dealership): DealershipFormValues {
  return {
    name: dealership.name,
    legalName: dealership.legalName ?? "",
    ein: dealership.ein ?? "",
    country: dealership.country,
    primaryTimezone: dealership.primaryTimezone,
    escalationsEnabled: dealership.escalationsEnabled,
  };
}

function toPayload(values: DealershipFormValues): UpdateDealershipDto {
  return UpdateDealershipSchema.parse({
    name: values.name.trim(),
    legalName: values.legalName?.trim() ? values.legalName.trim() : null,
    ein: values.ein?.trim() ? values.ein.trim() : null,
    country: values.country,
    primaryTimezone: values.primaryTimezone,
    escalationsEnabled: values.escalationsEnabled,
  });
}

export function DealershipOverviewPage() {
  const { dealershipId } = useParams();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const detailQuery = useQuery({
    queryKey: ["dealership", dealershipId],
    queryFn: () => adminApi.getDealership(dealershipId!),
    enabled: Boolean(dealershipId),
  });

  const statusMutation = useMutation({
    mutationFn: (input: UpdateDealershipDto) =>
      adminApi.updateDealership(dealershipId!, input),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["dealership", dealershipId] });
      await qc.invalidateQueries({ queryKey: ["dealerships"] });
      toast.success("Status updated");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const archiveMutation = useMutation({
    mutationFn: () => adminApi.archiveDealership(dealershipId!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["dealership", dealershipId] });
      await qc.invalidateQueries({ queryKey: ["dealerships"] });
      setArchiveConfirmOpen(false);
      toast.success("Dealership archived");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const unarchiveMutation = useMutation({
    mutationFn: () => adminApi.unarchiveDealership(dealershipId!),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["dealership", dealershipId] });
      await qc.invalidateQueries({ queryKey: ["dealerships"] });
      toast.success("Dealership unarchived");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (detailQuery.isLoading) {
    return <PageLoading message="Loading dealership…" />;
  }
  if (detailQuery.isError) {
    return (
      <PageError
        title="Couldn't load dealership"
        description="Try refreshing the page."
      />
    );
  }
  if (!detailQuery.data) return null;
  const detail = detailQuery.data;
  const aiUsers = detail.users.filter((u) => u.kind === "ai");
  const humanUsers = detail.users.filter((u) => u.kind !== "ai");

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        eyebrow="Overview"
        title={detail.name}
        description={detail.legalName ?? "Tenant configuration and operator roster."}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Metric
          icon={<Building2 className="h-4 w-4" />}
          label="Rooftops"
          value={String(detail.rooftops.length)}
        />
        <Metric
          icon={<Users className="h-4 w-4" />}
          label="Users"
          value={String(detail.users.length)}
        />
        <Metric
          icon={<Globe className="h-4 w-4" />}
          label="Timezone"
          value={detail.primaryTimezone}
        />
        <Metric
          icon={<Landmark className="h-4 w-4" />}
          label="EIN"
          value={detail.ein ?? "—"}
        />
      </div>

      <Section
        title="Tenant"
        description="Identity and status of this dealership."
        actions={
          <>
            {detail.archivedAt && (
              <Badge variant="destructive">Archived</Badge>
            )}
            <Badge variant={STATUS_VARIANT[detail.status]}>{detail.status}</Badge>
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" />
              Edit
            </Button>
            {detail.archivedAt ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => unarchiveMutation.mutate()}
                disabled={unarchiveMutation.isPending}
              >
                <ArchiveRestore className="h-4 w-4" />
                Unarchive
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setArchiveConfirmOpen(true)}
              >
                <Archive className="h-4 w-4" />
                Archive
              </Button>
            )}
          </>
        }
      >
        <dl className="grid gap-4 sm:grid-cols-2">
          <Pair label="Display name" value={detail.name} />
          <Pair label="Legal name" value={detail.legalName ?? "—"} />
          <Pair label="EIN" value={detail.ein ?? "—"} />
          <Pair label="Country" value={countryLabel(detail.country)} />
          <Pair label="Timezone" value={detail.primaryTimezone} />
        </dl>
        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm font-medium">Change status</span>
          <Select
            value={detail.status}
            onValueChange={(value) =>
              statusMutation.mutate({ status: value as Dealership["status"] })
            }
            disabled={statusMutation.isPending}
          >
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DealershipStatusSchema.options.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {statusMutation.isPending && (
            <span className="text-xs text-muted-foreground">Saving…</span>
          )}
        </div>
      </Section>

      <Section title="Rooftops" description="Locations operating under this dealership.">
        {detail.rooftops.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-5 w-5" />}
            title="No rooftops yet"
            description="Add rooftops from the dedicated Rooftops screen."
            action={
              <Button asChild variant="outline">
                <Link to={`/dealerships/${dealershipId}/rooftops`}>
                  Go to Rooftops
                </Link>
              </Button>
            }
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Address</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.rooftops.map((rooftop) => (
                    <TableRow key={rooftop.id}>
                      <TableCell className="font-medium">{rooftop.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{rooftop.status}</Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {[rooftop.addressLine1, rooftop.city, rooftop.state, rooftop.zip]
                          .filter(Boolean)
                          .join(", ") || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {detail.rooftops.map((rooftop) => (
                <MobileListRow
                  key={rooftop.id}
                  primary={rooftop.name}
                  secondary={rooftop.primaryEmail ?? rooftop.primaryPhone ?? "—"}
                  tertiary={
                    [rooftop.addressLine1, rooftop.city, rooftop.state, rooftop.zip]
                      .filter(Boolean)
                      .join(", ") || "—"
                  }
                  trailing={<Badge variant="secondary">{rooftop.status}</Badge>}
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      <Section title="AI users" description="Platform-managed AI operators for this dealership.">
        {aiUsers.length === 0 ? (
          <EmptyState
            icon={<Bot className="h-5 w-5" />}
            title="No AI user"
            description="A dealership AI user is provisioned automatically."
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last login</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aiUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {userDisplayName(user)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={user.status === "active" ? "success" : "secondary"}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(user.lastLoginAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {aiUsers.map((user) => (
                <MobileListRow
                  key={user.id}
                  primary={userDisplayName(user)}
                  secondary={user.email ?? "—"}
                  tertiary={formatDateTime(user.lastLoginAt)}
                  trailing={
                    <Badge variant={user.status === "active" ? "success" : "secondary"}>
                      {user.status}
                    </Badge>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      <Section title="Users" description="Operators with access to this dealership.">
        {humanUsers.length === 0 ? (
          <EmptyState
            icon={<Users className="h-5 w-5" />}
            title="No users yet"
            description="Invite operators from the dedicated Users screen."
            action={
              <Button asChild variant="outline">
                <Link to={`/dealerships/${dealershipId}/users`}>
                  Go to Users
                </Link>
              </Button>
            }
          />
        ) : (
          <>
            <TableBleed className="-my-6 hidden overflow-hidden rounded-b-lg md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last login</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {humanUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {userDisplayName(user)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email ?? "—"}</TableCell>
                      <TableCell>
                        <Badge variant={user.status === "active" ? "success" : "secondary"}>
                          {user.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {formatDateTime(user.lastLoginAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableBleed>
            <MobileList>
              {humanUsers.map((user) => (
                <MobileListRow
                  key={user.id}
                  primary={userDisplayName(user)}
                  secondary={user.email ?? "—"}
                  tertiary={formatDateTime(user.lastLoginAt)}
                  trailing={
                    <Badge variant={user.status === "active" ? "success" : "secondary"}>
                      {user.status}
                    </Badge>
                  }
                />
              ))}
            </MobileList>
          </>
        )}
      </Section>

      {/* Conditional mount (not `open` toggling) so the form's initial
          values reset on every open. */}
      {editing && (
        <DealershipFormDialog dealership={detail} onClose={() => setEditing(false)} />
      )}

      <Dialog open={archiveConfirmOpen} onOpenChange={(o) => !o && setArchiveConfirmOpen(false)}>
        <DialogContent preventDismiss>
          <DialogHeader>
            <DialogTitle>Archive dealership?</DialogTitle>
            <DialogDescription>
              All integrations, channels, imports, and AI replies will be frozen.
              No data is deleted. You can unarchive at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArchiveConfirmOpen(false)}
              disabled={archiveMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => archiveMutation.mutate()}
              disabled={archiveMutation.isPending}
            >
              {archiveMutation.isPending ? "Archiving…" : "Archive"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </CardTitle>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        <div className="truncate text-xl font-semibold" title={value}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function userDisplayName(user: {
  displayName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
}): string {
  if (user.displayName) return user.displayName;
  const composed = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return composed || user.email || "—";
}

function Pair({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid gap-1">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}

function DealershipFormDialog({
  dealership,
  onClose,
}: {
  dealership: Dealership;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const form = useZodForm(DealershipFormSchema, toEditValues(dealership));

  const updateMutation = useMutation({
    mutationFn: (input: DealershipFormValues) =>
      adminApi.updateDealership(dealership.id, toPayload(input)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["dealership", dealership.id] });
      await qc.invalidateQueries({ queryKey: ["dealerships"] });
      toast.success("Dealership updated");
      onClose();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  function submit() {
    const result = form.validate();
    if (!result.ok) return;
    updateMutation.mutate(result.data);
  }

  const { values, errors, setField } = form;
  const timezones = timezonesForCountry(values.country);

  // Switching country invalidates the other country's vocabulary:
  // snap the timezone into the new country's list.
  function onCountryChange(country: DealershipFormValues["country"]) {
    const nextTimezones = timezonesForCountry(country);
    form.patch({
      country,
      primaryTimezone: nextTimezones.includes(values.primaryTimezone)
        ? values.primaryTimezone
        : nextTimezones[0],
    });
  }

  // Once the admin has typed anything, block the implicit close paths
  // (outside click, Escape) so a stray click can't wipe the form.
  const isDirty =
    JSON.stringify(values) !== JSON.stringify(toEditValues(dealership));

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent preventDismiss={isDirty}>
        <DialogHeader>
          <DialogTitle>Edit dealership</DialogTitle>
          <DialogDescription>
            Identity, country, and primary timezone of this tenant.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Display name" required error={errors.name}>
            <Input
              value={values.name}
              onChange={(e) => setField("name", e.target.value)}
              autoFocus
              placeholder="Sunshine Auto Group"
            />
          </FormField>
          <FormField label="Legal name" optional error={errors.legalName}>
            <Input
              value={values.legalName ?? ""}
              onChange={(e) => setField("legalName", e.target.value)}
              placeholder="Sunshine Auto Holdings, LLC"
            />
          </FormField>
          <FormField label="EIN" optional error={errors.ein}>
            <Input
              value={values.ein ?? ""}
              onChange={(e) => setField("ein", e.target.value)}
              placeholder="12-3456789"
            />
          </FormField>
          <FormField label="Country" required error={errors.country}>
            <Select
              value={values.country}
              onValueChange={(v) => onCountryChange(v as DealershipFormValues["country"])}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DealershipCountrySchema.options.map((c) => (
                  <SelectItem key={c} value={c}>
                    {countryLabel(c)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField label="Primary timezone" required error={errors.primaryTimezone}>
            <Select
              value={values.primaryTimezone}
              onValueChange={(v) => setField("primaryTimezone", v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* A saved zone outside the country's list stays selectable
                    so the trigger isn't blank and an unrelated edit can't
                    silently overwrite it. */}
                {values.primaryTimezone && !timezones.includes(values.primaryTimezone) && (
                  <SelectItem value={values.primaryTimezone}>
                    {values.primaryTimezone}
                  </SelectItem>
                )}
                {timezones.map((tz) => (
                  <SelectItem key={tz} value={tz}>
                    {tz}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </FormField>
          <FormField
            label="Escalations enabled"
            optional
            error={errors.escalationsEnabled}
            description="When on, AI escalations notify the team (in-app + email), pause the AI, and open an SLA follow-up. When off, escalations are recorded silently — the lead is flagged as escalated but no one is notified and the AI keeps the conversation."
          >
            <Switch
              checked={values.escalationsEnabled}
              onCheckedChange={(v) => setField("escalationsEnabled", v)}
              aria-label={values.escalationsEnabled ? "Enabled" : "Disabled"}
            />
          </FormField>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
